import classnames from 'classnames'
import clr from 'color'
import type { PropsWithChildren, FC } from 'react'
import React, { useLayoutEffect, useRef } from 'react'

import styles from './CardWrapper.module.css'

type Props = {
  hatched?: boolean
  color?: string
  outlined?: boolean
  disabled?: boolean
}

export const CardWrapperColors = {
  default: '#f8F8F8',
  dark: '#718099'
}

const CardWrapper: FC<PropsWithChildren<Props>> = ({
  color = CardWrapperColors.default,
  outlined,
  hatched,
  disabled,
  children
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!containerRef.current) {
      return
    }

    const defaultColor = clr(color).rgb().string()
    const darkerColor = clr(color).darken(0.65).rgb().string()
    // for children component
    containerRef.current.style.color = darkerColor

    if (outlined) {
      containerRef.current.style.borderColor = darkerColor
    } else {
      containerRef.current.style.borderColor = defaultColor
    }
  }, [color, outlined])

  useLayoutEffect(() => {
    if (!containerRef.current) {
      return
    }

    const clrInstance = clr(color)
    const backgroundColor = clrInstance.rgb().string()

    if (hatched) {
      const hatchedColor = clrInstance.lighten(0.04).rgb().string()
      containerRef.current.style.background = `repeating-linear-gradient(-45deg, ${backgroundColor}, ${backgroundColor} 10px, ${hatchedColor} 10px, ${hatchedColor} 20px)`
    } else {
      containerRef.current.style.background = backgroundColor
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color])

  return (
    <div
      ref={containerRef}
      className={classnames(styles.Wrapper, {
        [styles.Disabled]: disabled
      })}
      data-testid='card-wrapper'
    >
      <div
        className={classnames(styles.Content)}
        data-testid='card-wrapper-content'
      >
        {children}
      </div>
    </div>
  )
}

export default CardWrapper
